<template>
  <div class="mb-5 text-h6 heading">
    Customer must agree to each of the following:
  </div>
  <SoCheckbox
    v-model="installationEssentials.supplyTurnedOff"
    label="Supply will be turned off for 30 mins per meter during installation"
  />
  <SoCheckbox
    v-model="installationEssentials.engineerCanAccessMainFuse"
    label="Engineer can access main fuse to turn supply off"
  />
  <SoCheckbox
    v-model="installationEssentials.adultPresent"
    class="mb-n2"
    label="Someone over the age of 18 will be present for the whole of the appointment"
  />
  <SoExpansionPanels
    v-if="installationEssentials.adultPresent"
    v-model="openPanels"
    class="mt-5 mb-n1 w-50"
  >
    <SoExpansionPanel no-padding>
      <template #title>Contact details</template>
      <template #alwaysVisible>
        <span v-if="requirePhoneNumber" data-testid="missing-phone-notice">
          The customer has no contact number on file. Add one to book the
          appointment.
        </span>
        <span v-else>If someone other than the customer will be present</span>
      </template>
      <div class="mt-7 contact-fields">
        <SoTextField
          ref="full-name"
          v-model="installationEssentials.contactFirstName"
          type="text"
          label="First name"
          :rules="nameRules('contactFirstName')"
          @update:model-value="editedNames.contactFirstName = true"
        />
        <SoTextField
          ref="full-name"
          v-model="installationEssentials.contactLastName"
          type="text"
          label="Last name"
          :rules="nameRules('contactLastName')"
          @update:model-value="editedNames.contactLastName = true"
        />
        <SoTextField
          ref="phone-number"
          v-model="installationEssentials.contactPhoneNumber"
          class="mt-3"
          type="text"
          label="Phone number"
          :rules="[rules.phoneNumber]"
          :error="missingPhoneNumberError"
        />
        <SoTextField
          ref="email"
          v-model="installationEssentials.contactEmail"
          class="mt-3"
          type="text"
          label="Email"
          :rules="[rules.email]"
        />
      </div>
    </SoExpansionPanel>
  </SoExpansionPanels>
  <!-- Opened by the step's Next action when the number can be saved -->
  <ImbpSaveContactNumberModal />
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoExpansionPanel from "@/components/expansionPanels/SoExpansionPanel.vue"
  import SoExpansionPanels from "@/components/expansionPanels/SoExpansionPanels.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import ImbpSaveContactNumberModal from "@/views/customer/imbp/modals/ImbpSaveContactNumberModal.vue"
  import rules from "@/validators/rules"
  import { IContact } from "@/models/contacts/contact.types"

  export default defineComponent({
    components: {
      SoCheckbox,
      SoExpansionPanels,
      SoExpansionPanel,
      SoTextField,
      ImbpSaveContactNumberModal,
    },
    props: {
      modelValue: {
        type: Object,
        default: () => ({}),
      },
      accountContact: {
        type: Object as PropType<IContact | null>,
        default: null,
      },
      requirePhoneNumber: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        installationEssentials: {
          supplyTurnedOff: false,
          engineerCanAccessMainFuse: false,
          adultPresent: false,
          contactFirstName: "",
          contactLastName: "",
          contactPhoneNumber: "",
          contactEmail: "",
        },
        rules: rules,
        openPanels: [] as number[],
        editedNames: {
          contactFirstName: false,
          contactLastName: false,
        },
      }
    },
    computed: {
      missingPhoneNumberError(): string | undefined {
        return this.requirePhoneNumber &&
          !this.installationEssentials.contactPhoneNumber.trim()
          ? "A contact number is required"
          : undefined
      },
    },
    watch: {
      requirePhoneNumber: {
        handler(isRequired: boolean) {
          if (!isRequired) return
          this.prefillFromAccountContact()
          this.openPanels = [0]
        },
        immediate: true,
      },
      installationEssentials: {
        handler(newValue) {
          this.$emit("update:modelValue", newValue)
        },
        deep: true,
      },
    },
    methods: {
      /*
        Names pre-filled from Junifer aren't checked until the agent edits
        them, so a name the agent didn't type never shows as an error.
      */
      nameRules(field: "contactFirstName" | "contactLastName") {
        return this.editedNames[field] ? [rules.nameRule] : []
      },
      prefillFromAccountContact() {
        const contact = this.accountContact
        if (!contact) return
        const details = this.installationEssentials
        details.contactFirstName ||= contact.firstName ?? ""
        details.contactLastName ||= contact.lastName ?? ""
        details.contactEmail ||= contact.email ?? ""
        details.contactPhoneNumber ||= contact.phone3?.trim() ?? ""
        this.$emit("update:modelValue", details)
      },
    },
  })
</script>

<style scoped>
  .heading {
    font-weight: 500 !important;
  }
  .contact-fields :deep(label) {
    font-size: 1rem;
  }
</style>
